<template>
  <!-- Port of the Laravel Pro panel date range picker (fyxify resources/js/components/partials/DateRangePicker.vue,
       jQuery daterangepicker 3.1.0) to Vue 3: same presets (Today … Last Month, then "Custom Range" opens the
       calendars), the matching preset shows active, the input is read-only (dates are picked in the calendar only).
       v-model = [start, end] as "MM/DD/YYYY" strings ([] = none); also emits change({ label, dates }). -->
  <div class="date-range-wrapper w-full relative">
    <input ref="input" type="text" readonly :class="inputClass" :value="displayValue" placeholder="Select Date"
      @input="onInputMask" @keypress="numericOnly" @change="validateRange" @keyup.enter="validateRange">
    <span v-if="showClearBtn && startDate && endDate"
      class="cursor-pointer absolute right-[10px] top-0 h-full flex items-center" @click.stop="clearDate">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6l12 12M18 6 6 18" stroke="#EE5851" stroke-width="2.4" stroke-linecap="round" />
      </svg>
    </span>
    <!-- pointer-events-none: a click on the icon lands on the input, which opens/closes the picker -->
    <span v-else-if="showCalendar"
      class="pointer-events-none absolute right-[10px] top-0 h-full flex items-center">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path d="M0 0h24v24H0z" fill="none" />
        <path fill="#8479e1"
          d="M18 4h-1V3a1 1 0 0 0-2 0v1H9V3a1 1 0 0 0-2 0v1H6a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3h12a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3M8 17a1 1 0 1 1 1-1a1 1 0 0 1-1 1m8 0h-4a1 1 0 0 1 0-2h4a1 1 0 0 1 0 2m3-6H5V7a1 1 0 0 1 1-1h1v1a1 1 0 0 0 2 0V6h6v1a1 1 0 0 0 2 0V6h1a1 1 0 0 1 1 1Z" />
      </svg>
    </span>
  </div>
</template>

<script>
import $ from "jquery";
import "daterangepicker";
import moment from "moment";
import "daterangepicker/daterangepicker.css";

const F = "MM/DD/YYYY";

export default {
  name: "DateRangePicker",
  props: {
    modelValue: { type: Array, default: () => [] },
    disableFuture: { type: Boolean, default: false },
    disablePast: { type: Boolean, default: false },
    showCalendar: { type: Boolean, default: false },
    disablePastDate: { type: Object, default: () => moment() },
    inputClass: {
      type: String,
      default: "w-full h-[40px] px-[12px] rounded-[8px] border-1 border-[#E1E5EF] bg-[#fff] text-[14px] text-[#333333] outline-none focus:border-[#8479E1] cursor-pointer",
    },
    // where the popup is appended (Laravel: ".content-wrapper"); body keeps it above modals / scroll boxes
    parentEl: { type: String, default: "body" },
    showClearBtn: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      startDate: null,
      endDate: null,
      lastValidInput: "", // last valid input for reverting invalid typing
    };
  },
  computed: {
    displayValue() {
      const format = (date) => {
        if (!date) return "__/__/____";
        return date.split("/").map((s, i) => s.padEnd(i === 2 ? 4 : 2, "_")).join("/");
      };
      if (!this.startDate && !this.endDate) return "__/__/____ - __/__/____";
      return `${format(this.startDate)} - ${format(this.endDate)}`;
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        if (!newVal || newVal.length !== 2) return;
        if (newVal[0] === this.startDate && newVal[1] === this.endDate) return;
        this.startDate = newVal[0];
        this.endDate = newVal[1];
        this.lastValidInput = `${newVal[0]} - ${newVal[1]}`;
        this.$nextTick(() => {
          const picker = this.picker();
          if (picker) {
            picker.setStartDate(moment(newVal[0], F));
            picker.setEndDate(moment(newVal[1], F));
          }
        });
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    if (!this.startDate) this.setDefaultDates();
    this.initDatePicker();
  },
  beforeUnmount() {
    const picker = this.picker();
    if (picker) picker.remove();
  },
  methods: {
    picker() {
      return this.$refs.input ? $(this.$refs.input).data("daterangepicker") : null;
    },

    // allow only digits and the separators
    numericOnly(event) {
      const allowedKeys = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "/", "-", " "];
      const navigationKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Delete", "Tab", "Enter"];
      if (!allowedKeys.includes(event.key) && !navigationKeys.includes(event.key)) {
        event.preventDefault();
      }
    },

    // mask while typing (no emit here): MM/DD/YYYY - MM/DD/YYYY
    onInputMask(event) {
      const input = event.target;
      const value = input.value;
      let cursor = input.selectionStart;
      const isDeletion = event.inputType && event.inputType.includes("delete");

      const formatPart = (dateStr) => {
        if (!dateStr) return "__/__/____";
        const segments = dateStr.split(/[/\-\s]/).filter(s => s !== "");
        if (segments.length === 0) return "__/__/____";
        const mm = (segments[0] || "").replace(/[^0-9]/g, "").substring(0, 2).padEnd(2, "_");
        const dd = (segments[1] || "").replace(/[^0-9]/g, "").substring(0, 2).padEnd(2, "_");
        const yyyy = (segments[2] || "").replace(/[^0-9]/g, "").substring(0, 4).padEnd(4, "_");
        return `${mm}/${dd}/${yyyy}`;
      };

      const parts = value.split(" - ");
      input.value = `${formatPart(parts[0] || "")} - ${formatPart(parts[1] || "")}`;

      if (!isDeletion) {
        if (cursor === 2 || cursor === 5 || cursor === 15 || cursor === 18) {
          cursor++;
        } else if (cursor === 10 || cursor === 11 || cursor === 12) {
          cursor = 13;
        }
      }
      input.setSelectionRange(cursor, cursor);
    },

    validateRange() {
      const picker = this.picker();
      if (picker && picker.isShowing) return; // the calendar handles it

      const value = this.$refs.input.value.replace(/_/g, "").trim();
      if (value === "/ / - / /" || value === "" || value === "Select Date") {
        this.startDate = "";
        this.endDate = "";
        this.lastValidInput = "";
        this.$emit("update:modelValue", []);
        return;
      }

      const dates = value.split(" - ");
      const startStr = dates[0]?.trim();
      const endStr = dates[1]?.trim() || startStr;
      const startMoment = moment(startStr, F, true);
      const endMoment = moment(endStr, F, true);
      const isStartValid = startMoment.isValid() && startMoment.year() >= 1970;
      const isEndValid = endMoment.isValid() && endMoment.year() >= 1970;

      if (isStartValid && isEndValid && !startMoment.isAfter(endMoment)) {
        this.startDate = startMoment.format(F);
        this.endDate = endMoment.format(F);
        this.lastValidInput = `${this.startDate} - ${this.endDate}`;
        this.$refs.input.value = this.lastValidInput;
        this.$emit("update:modelValue", [this.startDate, this.endDate]);
        this.$emit("change", { label: "Custom", dates: [this.startDate, this.endDate] });
        if (picker) {
          picker.setStartDate(startMoment);
          picker.setEndDate(endMoment);
        }
      } else if (this.lastValidInput) {
        this.$refs.input.value = this.lastValidInput;
        const parts = this.lastValidInput.split(" - ");
        this.startDate = parts[0];
        this.endDate = parts[1];
        this.$emit("update:modelValue", [this.startDate, this.endDate]);
      } else {
        this.setTodayRange();
      }
    },

    setTodayRange() {
      const today = moment().format(F);
      this.startDate = today;
      this.endDate = today;
      this.lastValidInput = `${today} - ${today}`;
      this.$refs.input.value = this.lastValidInput;
      this.$emit("update:modelValue", [this.startDate, this.endDate]);
      this.$emit("change", { label: "Today", dates: [this.startDate, this.endDate] });
      const picker = this.picker();
      if (picker) {
        picker.setStartDate(moment());
        picker.setEndDate(moment());
      }
    },

    clearDate() {
      this.startDate = "";
      this.endDate = "";
      const picker = this.picker();
      if (picker) {
        picker.setStartDate(moment());
        picker.setEndDate(moment());
        const container = $(picker.container);
        container.find(".ranges li").removeClass("active");
        container.find(".drp-calendar td").removeClass("active in-range start-date end-date");
        container.removeClass("show-calendar");
      }
      this.$emit("update:modelValue", []);
      this.$emit("change", { label: "Select Date", dates: [] });
    },

    // no value → Month to Date, like Laravel
    setDefaultDates() {
      this.startDate = moment().startOf("month").format(F);
      this.endDate = moment().format(F);
      this.lastValidInput = `${this.startDate} - ${this.endDate}`;
    },

    initDatePicker() {
      const self = this;
      const option = {
        startDate: moment(this.startDate, F),
        endDate: moment(this.endDate, F),
        autoApply: true,
        showDropdowns: false,
        showCustomRangeLabel: false,
        showCalendarInputs: false,
        parentEl: this.parentEl,
        locale: { format: F, applyLabel: "OK", cancelLabel: "Cancel" },
        maxDate: this.disableFuture ? moment() : false,
        minDate: this.disablePast ? this.disablePastDate : false,
      };

      if (!this.disablePast) {
        option.showCustomRangeLabel = true;
        option.ranges = {
          "Today": [moment(), moment()],
          "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "Month to Date": [moment().startOf("month"), moment()],
          "Year to Date": [moment().startOf("year"), moment()],
          "Last Year to Date": [moment().subtract(1, "year").startOf("year"), moment()],
          "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        };
      }

      $(this.$refs.input).daterangepicker(option, function (start, end, label) {
        if (start.year() < 1970 || end.year() < 1970) {
          const picker = self.picker();
          if (picker && self.startDate && self.endDate) {
            picker.setStartDate(moment(self.startDate, F));
            picker.setEndDate(moment(self.endDate, F));
          }
          return;
        }
        self.startDate = start.format(F);
        self.endDate = end.format(F);
        self.lastValidInput = `${self.startDate} - ${self.endDate}`;
        self.$emit("change", { label: label || "Custom Range", dates: [self.startDate, self.endDate] });
        self.$emit("update:modelValue", [self.startDate, self.endDate]);
        self.$refs.input.blur();
      });
      // tablet+: under the input, as wide as it, no taller than the space below it (phones: bottom sheet in CSS)
      const fit = () => {
        if (window.innerWidth < 640) return;
        const box = this.picker()?.container?.[0];
        if (!box) return;
        box.style.width = `${this.$refs.input.offsetWidth}px`;
        box.style.maxHeight = `${Math.max(240, window.innerHeight - box.getBoundingClientRect().top - 8)}px`;
      };
      $(this.$refs.input).on("show.daterangepicker showCalendar.daterangepicker", fit);
      // title above the presets list
      if (option.ranges) {
        $(this.picker().container).find(".ranges").prepend(
          '<p style="margin:0;padding:8px 12px;font:500 13px Roboto,sans-serif;color:#333333;border-bottom:1px solid #EFEFEF">Select Date Range</p>'
        );
      }
    },

    // open the picker from outside (ref.openDatePicker())
    openDatePicker() {
      this.$nextTick(() => {
        const picker = this.picker();
        if (picker) picker.show();
      });
    },
  },
};
</script>
